import React, { useState, useEffect } from 'react';
import { Input, Select, Button, message } from 'antd';

import { SearchOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { debounce } from 'lodash';
import { GET_ADMIN_USERS_FOR_ROLES } from '../../services/userAPI';
import NotificationSystem from "../clanCrew/notificationSystem";

import './roleManagement.css';
import PageBreadcrumb from '../../reusableField/PageBreadcrumb';

const RoleManagement = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState({});
    const [roleOptions, setRoleOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showNotificationSystem, setShowNotificationSystem] = useState(false);

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const accountId = location.state?.accountId;
    const roleId = location.state?.roleId;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const name = location.state?.name;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;
    const accountName = location.state?.accountName;

    // Lazy query for users
    const [fetchUserListt, { data: userListDataa, loading: userListLoadingg, error: userListErrorr }] = useLazyQuery(
        GET_ADMIN_USERS_FOR_ROLES,
        {
            fetchPolicy: 'cache-and-network',
            variables: {
                roleName: 'clan crew',
                jobroleId: roleId ? parseInt(roleId) : null,
                filterByName: '', // Initialize with empty string for search
            },
        }
    );

    // Debounced fetchUserListt function
    const debouncedFetchUserListt = debounce((value) => {
        if (roleId) {
            fetchUserListt({
                variables: {
                    roleName: 'clan crew',
                    jobroleId: parseInt(roleId),
                    filterByName: value || '',
                },
            });
        }
    }, 500);

    // Fetch users on component mount and roleId change
    useEffect(() => {
        if (roleId) {
            fetchUserListt({
                variables: {
                    roleName: 'clan crew',
                    jobroleId: parseInt(roleId),
                    filterByName: '',
                },
            });
        }
    }, [roleId, fetchUserListt]);

    // Update users and role options with fetched data
    useEffect(() => {
        if (userListDataa?.getAdminUsersForRoles?.data) {
            const mappedUsers = userListDataa.getAdminUsersForRoles.data
                .filter(user => user.firstName)
                .map(user => ({
                    id: user.id,
                    name: `${user.firstName} ${user.surname || ''}`.trim(),
                    firstName: user.firstName,
                    profilePicture: user.profilePicture,
                    role: user.roleName,
                    email: user.email,
                    contactNumber: user.contactNumber,
                }));
            setUsers(mappedUsers);

            // Update role options
            const fetchedRoles = userListDataa.getAdminUsersForRoles.data
                .filter(user => user.firstName)
                .map(user => ({
                    value: user.firstName,
                    label: user.firstName,
                }));
            const uniqueRoles = fetchedRoles.filter((role, index, self) =>
                index === self.findIndex(r => r.value === role.value)
            );
            setRoleOptions(uniqueRoles.length > 0 ? uniqueRoles : []);
        } else if (userListErrorr) {
            message.error('Failed to fetch users: ' + userListErrorr.message);
            setUsers([]);
            setRoleOptions([]);
        }
    }, [userListDataa, userListErrorr]);

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedFetchUserListt(value);
    };

    // Handle role selection change
    const handleRoleChange = (userId, value) => {
        setSelectedRoles((prev) => ({
            ...prev,
            [userId]: value,
        }));
    };

    // Handle save button click
    const handleSave = (userId) => {
        const selectedRole = selectedRoles[userId];
        if (!selectedRole) {
            message.error('Please select a role before saving.');
            return;
        }
        // Update the user's assigned role (mock update; replace with API call)
        setUsers((prevUsers) =>
            prevUsers.map((user) =>
                user.id === userId ? { ...user, role: selectedRole } : user
            )
        );
        message.success(`Role updated for ${users.find(u => u.id === userId)?.name}`);
        // Clear the selected role for this user after saving
        setSelectedRoles((prev) => {
            const newRoles = { ...prev };
            delete newRoles[userId];
            return newRoles;
        });
    };

    const handleBack = () => {
        navigate('/admin');
    };

    const handleBackToViewRole = () => {
        const safeRoleId = roleId || location.state?.roleId;
        const safeAccountName = accountName || location.state?.accountName;

        navigate('/view-role', {
            state: {
                roleId: safeRoleId,
                accountId: accountId || location.state?.accountId,
                accountName: safeAccountName,
                name: name || location.state?.name,
                dartsNeeded: dartsNeeded || location.state?.dartsNeeded,
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded,
                dartsFormNeeded: dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution: mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };

    const handleDiscardClick = () => {
        navigate('/admin');
    };

    // Loading or error state rendering
    if (userListLoadingg) return (
        <div className="flex justify-center items-center h-screen">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
        </div>
    );
    if (userListErrorr) return <div className="text-center text-red-600">Error loading users: {userListErrorr.message}</div>;

    const handleCardClick = (userId) => {
        navigate('/role-management-view', {
            state: {
                userId,
                roleId,
                accountId,
                accountName,
                name,
                dartsNeeded,
                clanNeeded,
                syncNeeded,
                dartsFormNeeded,
                pocName,
                pocEmail,
                pocNumber,
                numberLogin,
                emailLogin,
                mandatoryTimeDistribution,
                targetEditDays,
                inputEditDays,
            },
        });
    };

    return (
        <div className="p-6 mx-auto min-h-screen">
            <PageBreadcrumb homePath="/admin" onBack={handleBackToViewRole} style={{ marginTop: '-3.5rem' }} extraButtons={
                <button
                    onClick={() => setShowNotificationSystem(true)}
                    className="px-4 py-2 bg-[#AD0101] text-white rounded font-inter text-sm"
                >
                    Notification History
                </button>
            }>
                <span onClick={handleBack} style={{ cursor: 'pointer' }}>{accountName}</span>
                <span>&gt;</span>
                <span onClick={handleBackToViewRole} style={{ cursor: 'pointer' }}>{name}</span>
                <span>&gt;</span>
                <span style={{ fontWeight: 'bold' }}>Darts</span>
            </PageBreadcrumb>

            <h2 className="font-inter text-2xl font-semibold mb-4 text-gray-800">Role Management</h2>
            {/* Search Box */}
            <div className="mb-6">
                <Input
                    placeholder="Search by name..."
                    onChange={handleSearchChange}
                    value={searchTerm}
                    allowClear
                    suffix={<SearchOutlined className="text-gray-400" />}
                    className="w-full p-2 rounded border-gray-300 focus:border-red-600"
                />
            </div>
            {/* User Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">

                {users.length > 0 ? (
                    users.map((user) => {
                        const userAssignedRole = user.role;
                        const filteredOptions = roleOptions.map(option => ({
                            ...option,
                            disabled: option.value === userAssignedRole,
                            label: option.value === userAssignedRole ? `${option.label} (already assigned)` : option.label,
                        }));

                        return (
                            <div
                                onClick={() => handleCardClick(user.id)}
                                key={user.id}
                                className="flex flex-col items-center p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                            >
                                <img
                                    src={user.profilePicture}
                                    alt={user.name}
                                    className="w-20 h-20 rounded-full object-cover mb-3"
                                    onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/80'; // Fallback image
                                    }}
                                />
                                <p className="font-inter text-sm text-gray-800 mb-1">{user.surname}{user.name}</p>
                            </div>
                        );
                    })
                ) : (
                    <p className="col-span-full text-center text-gray-600 font-inter text-sm">
                        No users found.
                    </p>
                )}
            </div>
            {showNotificationSystem && (
                <NotificationSystem
                    visible={showNotificationSystem}
                    onCancel={() => setShowNotificationSystem(false)}
                />
            )}
        </div>

    );
};

export default RoleManagement;